/**
 * Which baked models have an optional enhanced counterpart.
 *
 * `data/enhanced` is an experiment and is built by a separate, optional step
 * (`bun run enhanced`), so the app has to treat its absence as normal: a
 * missing or unreadable manifest means "no enhanced models exist", never an
 * error. That is why this returns an empty index rather than throwing, and why
 * the viewer's comparison control simply does not appear when the index is
 * empty.
 *
 * The manifest keys on the same ids the bake manifest uses — `models/FOKDR1`,
 * `models/FOKDR1@Richthofen` — so a paint scheme maps one-to-one and switching
 * ace in the viewer switches both models together.
 *
 * **Empty is not one answer, it is three.** The index comes out empty for every
 * kind of absence, which is what keeps a machine with no `data/enhanced`
 * working — but *which* absence is recorded (`EnhancedIndexStatus`) and said out
 * loud. A host that answers `/enhanced/manifest.json` with its SPA shell and a
 * 200 is not a host with no enhanced models; it is a host that has them and
 * cannot serve them, and only the second is worth a word in the console.
 * `enhanced-diagnostics.ts` has the bug report that distinction costs.
 */

import { createSignal } from "solid-js";
import { warnEnhancedOnce } from "./enhanced-diagnostics.ts";
import { ladderOf, type EnhancedRung } from "./enhanced-lod.ts";

export interface EnhancedManifest {
  /** Which baked models the tool was asked to build. Informational; `entries` is what the app looks things up in. */
  readonly models?: readonly string[];
  /**
   * The LOD ladder the models were built with, as `tools/blender/rb_enhanced/detail.py`
   * states it. Absent from a `data/enhanced` built before the ladder existed,
   * which `ladderOf` answers with the same numbers.
   */
  readonly ladder?: readonly EnhancedRung[];
  readonly entries: readonly {
    readonly id: string;
    readonly path: string;
    /**
     * The `.glb`'s decoded length in bytes, written by `tools/enhanced`.
     *
     * Optional because a `data/enhanced` built before G58 does not carry it, and
     * that tree is still perfectly loadable — the only thing missing is the
     * loading bar's denominator, and `streamed-body.ts` degrades to saying
     * nothing rather than to saying something wrong.
     */
    readonly bytes?: number;
  }[];
}

/**
 * Where an enhanced model lives, and how big it is once decoded.
 *
 * The size rides with the path rather than in a second map because the two are
 * read together at exactly one call site and a lookup that can return a path
 * without its size is a lookup someone will forget to make twice.
 */
export interface EnhancedEntry {
  readonly path: string;
  /** Decoded bytes, or `undefined` for a tree built before the field existed. */
  readonly bytes?: number;
}

export type EnhancedIndex = ReadonlyMap<string, EnhancedEntry>;

export const EMPTY_ENHANCED_INDEX: EnhancedIndex = new Map<string, EnhancedEntry>();

/** Turns a fetched manifest into an id-to-entry lookup. Exported for tests; `fetchEnhancedIndex` is what the viewer calls. */
export function indexOf(manifest: EnhancedManifest | undefined): EnhancedIndex {
  if (!manifest || !Array.isArray(manifest.entries)) return EMPTY_ENHANCED_INDEX;
  return new Map(
    manifest.entries.map((entry) => {
      // A `bytes` that is not a positive finite number is dropped rather than
      // passed on: the denominator's whole job is to be trustworthy, and a
      // hand-edited or truncated manifest must degrade to "no signal", never to
      // a bar driven by a zero or a NaN.
      const bytes = typeof entry.bytes === "number" && Number.isFinite(entry.bytes) && entry.bytes > 0 ? entry.bytes : undefined;
      return [entry.id, { path: entry.path, bytes }] as const;
    }),
  );
}

/**
 * The ladder the fetched manifest declares, or the compatibility default.
 * Shared for the whole document alongside the index, and for the same reason:
 * an offline tool wrote it and it cannot change under a running tab.
 */
export function enhancedLadder(): readonly EnhancedRung[] {
  return cachedLadder;
}

let cachedLadder: readonly EnhancedRung[] = ladderOf(undefined);

/**
 * Why the index is what it is.
 *
 * - `ready` — a manifest was fetched and read. It may still hold zero entries;
 *   that is a run of the tool that built nothing, and it is not a fault.
 * - `absent` — nothing answered: a 404 from `dev-server.ts` on a machine that
 *   never ran `bun run enhanced`, or a fetch that could not be made at all.
 *   The designed, ordinary case.
 * - `misserved` — **something answered 200 that is not a manifest.** The models
 *   may well be on disk; the route in front of them is not wired up, and a SPA
 *   catch-all is answering for them. A deployment fault, never a normal state.
 */
export type EnhancedIndexStatus = "ready" | "absent" | "misserved";

export const ENHANCED_MANIFEST_PATH = "/enhanced/manifest.json";

let cachedStatus: EnhancedIndexStatus = "absent";

/** The reason behind the current index — `absent` until the one fetch lands. */
export function enhancedIndexStatus(): EnhancedIndexStatus {
  return cachedStatus;
}

/** True for a body that parsed as JSON and carries the one field the app looks things up in. */
function isManifest(candidate: unknown): candidate is EnhancedManifest {
  return typeof candidate === "object" && candidate !== null && Array.isArray((candidate as EnhancedManifest).entries);
}

export async function fetchEnhancedIndex(): Promise<EnhancedIndex> {
  let body: string;
  try {
    const response = await fetch(ENHANCED_MANIFEST_PATH);
    if (!response.ok) {
      cachedStatus = "absent";
      return EMPTY_ENHANCED_INDEX;
    }
    body = await response.text();
  } catch {
    cachedStatus = "absent";
    return EMPTY_ENHANCED_INDEX;
  }

  // Read as text and parsed here rather than through `response.json()`, because
  // the interesting failure is a **200 that is not JSON at all**: a host with no
  // `/enhanced/*` route answers this with the SPA's own `index.html`. That is a
  // deployment fault wearing a success code, and it must not read as "this
  // machine has no enhanced models".
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    cachedStatus = "misserved";
    warnEnhancedOnce(
      `${ENHANCED_MANIFEST_PATH} answered 200 with something that is not JSON (starts ${JSON.stringify(body.slice(0, 40))}). ` +
        "A static host is serving its SPA fallback for /enhanced/* — stage data/enhanced into the served tree " +
        "(`bun run dist`, which hardlinks it to dist/enhanced/) or give the host an /enhanced/* route. " +
        "Until then ENHANCED MODELS has nothing to switch to and every aeroplane draws its baked .TBL.",
    );
    return EMPTY_ENHANCED_INDEX;
  }
  if (!isManifest(parsed)) {
    cachedStatus = "misserved";
    warnEnhancedOnce(`${ENHANCED_MANIFEST_PATH} parsed as JSON but carries no \`entries\` array — it is not an enhanced manifest.`);
    return EMPTY_ENHANCED_INDEX;
  }

  cachedStatus = "ready";
  cachedLadder = ladderOf(parsed.ladder);
  return indexOf(parsed);
}

const [cached, setCached] = createSignal<EnhancedIndex | undefined>(undefined);
let pending: Promise<EnhancedIndex> | undefined;

/**
 * The index, fetched **once** for the whole document and shared by every caller
 * — the `/assets` panes, the career turntable and every aeroplane `/fly` puts
 * in the sky. Same ownership as `aircraft-mesh.ts`'s ramp tables and propeller
 * hubs: the manifest is written by an offline tool and cannot change under a
 * running tab, so a second fetch could only ever return the same bytes.
 */
export function enhancedIndexOnce(): Promise<EnhancedIndex> {
  pending ??= fetchEnhancedIndex().then((index) => {
    setCached(index);
    return index;
  });
  return pending;
}

/**
 * The same index for a reactive caller: `undefined` until the one fetch lands.
 *
 * A plain signal filled by that fetch rather than a `createResource` at module
 * scope: a resource is a computation, and one created outside a root is never
 * disposed and warns about it. Nothing here needs a resource's refetch or error
 * states — the absence of a manifest is a valid answer, not a failure.
 */
export function enhancedIndex(): EnhancedIndex | undefined {
  void enhancedIndexOnce();
  return cached();
}
